import { api } from '@/api/client'

export interface ConversationListItem {
  id: number
  client: string
  environment: string
  title: string
  last_message_at: string | null
  unread: number
  avatar_url?: string | null
  /** Conversa em mute pelo caller (dm/org). Usado p/ silenciar o som de notificação. */
  is_muted?: boolean
  last_message: {
    id: number
    severity: string | null
    body: string | null
    created_at: string
  } | null
}

export interface ChatMessageReplyTo {
  id: number
  kind: string
  author_name: string | null
  snippet: string
}

export interface ChatMessageMeta {
  mention_all?: boolean
  reply_to?: ChatMessageReplyTo
  [key: string]: unknown
}

/** Anexo já no S3, ainda sem linha no banco — o que o cliente manda ao perguntar. */
export interface AskAttachmentRef {
  s3_key: string
  filename: string
  mime_type: string
  size_bytes: number
}

export interface ChatAttachment {
  id: number
  filename: string
  mime_type: string
  size_bytes: number
  url?: string | null
}

export interface ChatMessage {
  id: number
  kind: 'alert' | 'system' | 'bot' | 'human'
  severity: string | null
  body: string | null
  metadata: ChatMessageMeta | null
  source_event_id: number | null
  author_user_id: number | null
  author_name?: string | null
  author_avatar_url?: string | null
  created_at: string
  category?: string | null
  read?: boolean
  edited?: boolean
  deleted?: boolean
  reactions?: { emoji: string; count: number }[]
  my_reaction?: string | null
  attachments?: ChatAttachment[]
}

export interface MessageReceiptUser {
  user_id: number
  full_name: string | null
  email: string
}

/** Retorno de GET /conversations/{cid}/messages/{mid}/info (owner-only). */
export interface MessageInfo {
  read: MessageReceiptUser[]
  delivered_not_read: MessageReceiptUser[]
  not_delivered: MessageReceiptUser[]
}

export type ChatTab = 'conversas' | 'alertas' | 'atendente'

export interface ConversationMember {
  id: number
  name: string
  email: string
  avatar_url?: string | null
}

export interface MembersPage {
  items: ConversationMember[]
}

export interface MentionableUser {
  id: number
  name: string
  email: string
  /** true no @Atendente (usuário-bot). O Composer roteia esse item para /ask. */
  is_bot?: boolean
  /** Presente só no bot de um robô participante: a menção vai à rota DELE. */
  robo?: { bot_user_id: number; public_id: string; dono_user_id: number; nome: string }
}

export interface MessagesPage {
  items: ChatMessage[]
  next_cursor: number | null
  conversation?: ConversationEnvelope
}

/** Envelope de conversa retornado junto com os messages (GET /conversations/{cid}/messages). */
export interface ConversationEnvelope {
  id: number
  conv_type: 'alert' | 'dm' | 'org' | 'chamado' | 'dba'
  title: string | null
  organization_id: number | null
  is_muted?: boolean
  attachments_supported?: boolean
  /** True quando é uma DM 1:1 com o bot @Atendente (envio via /ask + anexos). */
  is_atendente_dm?: boolean
  is_robo_dm?: boolean
  avatar_url?: string | null
  can_manage_avatar?: boolean
  /** Conversa de atendimento de um chamado (spec 2026-09-26 §4.3): tudo vai ao @Atendente. */
  is_chamado?: boolean
  task_request_id?: number | null
  protocolo?: string | null
}

export interface EstadoDoPlano {
  estado: 'aguardando' | 'aprovado' | 'executado' | 'expirado' | 'recusado' | 'substituido'
  pode_aprovar: boolean
  motivo: string | null
  expira_em: string | null
  solicitante: string
  e_o_solicitante: boolean
}

export const chatApi = {
  listConversations: () =>
    api
      .get<{ items: ConversationListItem[] }>('/v1/conversations')
      .then((r) => r.data.items),

  listMessages: (
    cid: number,
    params?: { before?: number; limit?: number; tab?: ChatTab },
  ) =>
    api
      .get<MessagesPage>(`/v1/conversations/${cid}/messages`, { params })
      .then((r) => r.data),

  members: (cid: number) =>
    api
      .get<MembersPage>(`/v1/conversations/${cid}/members`)
      .then((r) => r.data.items),

  messageInfo: (cid: number, mid: number) =>
    api
      .get<MessageInfo>(`/v1/conversations/${cid}/messages/${mid}/info`)
      .then((r) => r.data),

  // messageId omitido → marca a conversa inteira como lida (MAX no servidor),
  // comportamento WhatsApp. Com id, marca até aquela mensagem (ticks de leitura).
  markRead: (cid: number, messageId?: number) =>
    api
      .post(`/v1/conversations/${cid}/read`, messageId != null ? { message_id: messageId } : {})
      .then((r) => r.data),

  // Presença efêmera "digitando" — best-effort, ignora erros (não bloqueia digitar).
  sendTyping: (cid: number) =>
    api.post(`/v1/conversations/${cid}/typing`).then((r) => r.data).catch(() => {}),

  wsTicket: () =>
    api
      .post<{ ticket: string; expires_in: number }>('/v1/ws/ticket')
      .then((r) => r.data),

  askBot: (
    cid: number,
    text: string,
    opts?: {
      alertMessageId?: number
      shared?: boolean
      attachments?: { s3_key: string; filename: string; mime_type: string; size_bytes: number }[]
    },
  ) =>
    api
      .post<{ human_message_id: number; bot_message_id: number; answer: string }>(
        `/v1/conversations/${cid}/ask`,
        {
          text,
          alert_message_id: opts?.alertMessageId,
          shared: opts?.shared,
          ...(opts?.attachments ? { attachments: opts.attachments } : {}),
        },
        { timeout: 20000 },
      )
      .then((r) => r.data),

  // O robô de alguém, no grupo (TASK-12125). Teto longo: o turno pode consultar o
  // @DevOps e, quando o grupo não pode ver tudo, ainda continuar na conversa privada.
  askRoboNoGrupo: (cid: number, botUserId: number, text: string) =>
    api
      .post<{ human_message_id: number; bot_message_id: number; answer: string;
              dm_conversation_id: number | null }>(
        `/v1/conversations/${cid}/robos/${botUserId}/ask`, { text }, { timeout: 120000 },
      )
      .then((r) => r.data),

  confirmAction: (cid: number, mid: number) =>
    api
      .post(`/v1/conversations/${cid}/actions/${mid}/confirm`)
      .then((r) => r.data),

  declineAction: (cid: number, mid: number) =>
    api
      .post(`/v1/conversations/${cid}/actions/${mid}/decline`)
      .then((r) => r.data),

  // Escolha de uma alternativa do laudo de disco (spec 2026-09-27 §5). Só o
  // verbo vai no corpo: máquina e tenant o servidor tira da âncora da conversa.
  escolherAlternativa: (cid: number, mid: number, verbo: string) =>
    api
      .post<{ message_id: number; proposed_action: Record<string, unknown> }>(
        `/v1/conversations/${cid}/messages/${mid}/alternativas/escolher`,
        { verbo },
      )
      .then((r) => r.data),

  // Estado da segunda aprovação para QUEM OLHA (spec 2026-09-27 §12). Plano de
  // tenant que a pessoa não vê responde 404.
  estadoDoPlano: (planId: string) =>
    api
      .get<EstadoDoPlano>(`/v1/host-orders/plans/${encodeURIComponent(planId)}/estado`)
      .then((r) => r.data),

  // "Isso não bate" no cartão do laudo (TASK-10428). `motivo` é de conjunto
  // fechado no servidor (`diagnostics.discordancia.MOTIVOS`); o tipo daqui NÃO
  // o redeclara como union — o cartão já restringe as opções que oferece, e
  // duas listas do mesmo conjunto divergem no primeiro valor novo.
  discordarDoLaudo: (
    cid: number,
    mid: number,
    body: { motivo: string; comentario?: string },
  ) =>
    api
      .post(`/v1/conversations/${cid}/diagnostico/${mid}/discordancia`, body)
      .then((r) => r.data),

  // Aplica uma sugestão do @Atendente num chamado (hoje: comentário) — como
  // ação do humano, gated pela permissão de chamados no tenant do chamado.
  // `assigneeUserId`: escolha do humano no cartão de atribuir (TASK-11421) —
  // vence a sugestão do Atendente. Os demais cartões não mandam corpo.
  // `texto`: o fechamento que o atendente EDITOU no cartão de finalizar — vence o proposto.
  applyChamadoAction: (cid: number, mid: number, assigneeUserId?: number, texto?: string) =>
    api
      .post(
        `/v1/conversations/${cid}/chamado-actions/${mid}/apply`,
        assigneeUserId === undefined && texto === undefined
          ? undefined
          : {
              ...(assigneeUserId === undefined ? {} : { assignee_user_id: assigneeUserId }),
              ...(texto === undefined ? {} : { texto }),
            },
      )
      .then((r) => r.data),

  postMessage: (
    cid: number,
    text: string,
    opts?: {
      mentionUserIds?: number[]
      mentionAll?: boolean
      replyToMessageId?: number | null
      channel?: 'conversas' | 'alertas'
      attachments?: { s3_key: string; filename: string; mime_type: string; size_bytes: number }[]
    },
  ) =>
    api
      .post(
        `/v1/conversations/${cid}/messages`,
        {
          text,
          mention_user_ids: opts?.mentionUserIds ?? [],
          mention_all: opts?.mentionAll ?? false,
          reply_to_message_id: opts?.replyToMessageId ?? null,
          channel: opts?.channel ?? 'conversas',
          ...(opts?.attachments ? { attachments: opts.attachments } : {}),
        },
        { timeout: 20000 },
      )
      .then((r) => r.data),

  mentionable: (cid: number, q: string) =>
    api
      .get(`/v1/conversations/${cid}/mentionable`, { params: { q } })
      .then((r) => r.data.items as MentionableUser[]),

  editMessage: (cid: number, mid: number, text: string) =>
    api
      .patch(`/v1/conversations/${cid}/messages/${mid}`, { text })
      .then((r) => r.data),

  deleteMessage: (cid: number, mid: number) =>
    api
      .delete(`/v1/conversations/${cid}/messages/${mid}`)
      .then((r) => r.data),

  hideMessageForMe: (cid: number, mid: number) =>
    api
      .delete(`/v1/conversations/${cid}/messages/${mid}/for-me`)
      .then((r) => r.data),

  /** Corta a memória do @Atendente para quem pede; nada é apagado (#24784). */
  recomecarAtendente: (cid: number) =>
    api
      .post<{ message_id: number }>(`/v1/chat/conversations/${cid}/recomecar-atendente`)
      .then((r) => r.data),

  hideConversation: (cid: number, hidden: boolean) =>
    api
      .put(`/v1/chat/conversations/${cid}/hide`, { hidden })
      .then((r) => r.data),

  setReaction: (cid: number, mid: number, emoji: string) =>
    api
      .put(`/v1/conversations/${cid}/messages/${mid}/reaction`, { emoji })
      .then((r) => r.data),

  removeReaction: (cid: number, mid: number) =>
    api
      .delete(`/v1/conversations/${cid}/messages/${mid}/reaction`)
      .then((r) => r.data),

  presignAttachment: (
    cid: number,
    f: { filename: string; mimeType: string; sizeBytes: number },
  ) =>
    api
      .post(`/v1/conversations/${cid}/attachments/presign`, {
        filename: f.filename,
        mime_type: f.mimeType,
        size_bytes: f.sizeBytes,
      })
      .then(
        (r) =>
          r.data as {
            supported: boolean
            presigned_put_url?: string
            s3_key?: string
          },
      ),

  uploadToS3: async (presignedUrl: string, file: File): Promise<void> => {
    // PUT direto no S3 com Content-Type EXATO; NÃO usar a instância `api`
    // (o presign travou ContentType/ContentLength; headers extras invalidam a assinatura).
    const res = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    })
    if (!res.ok) throw new Error(`s3_put_failed_${res.status}`)
  },

  attachmentDownloadUrl: (attachmentId: number) =>
    api
      .get(`/v1/chat/attachments/${attachmentId}/download`)
      .then(
        (r) => r.data as { url: string; filename: string; mime_type: string },
      ),

  convAvatarPresign: (
    cid: number,
    f: { filename: string; mimeType: string; sizeBytes: number },
  ) =>
    api
      .post(`/v1/conversations/${cid}/avatar/presign`, {
        filename: f.filename,
        mime_type: f.mimeType,
        size_bytes: f.sizeBytes,
      })
      .then(
        (r) =>
          r.data as {
            supported: boolean
            presigned_put_url?: string
            s3_key?: string
          },
      ),

  convAvatarConfirm: (cid: number, s3_key: string) =>
    api
      .post(`/v1/conversations/${cid}/avatar/confirm`, { s3_key })
      .then((r) => r.data as { avatar_url: string | null }),

  convAvatarDelete: (cid: number) =>
    api
      .delete(`/v1/conversations/${cid}/avatar`)
      .then((r) => r.data),
}
